import { Alert, TextField } from '@cmsgov/ds-medicare-gov';
import { ChangeEventHandler, useEffect, useRef, useState } from 'react';
import ErrorAlert from '../../common/components/ErrorAlert';
import PasswordSection from '../../common/components/PasswordSection';
import Button from '../../common/components/button';
import Checkbox from '../../common/components/checkbox';
import Div from '../../common/components/div';
import H1 from '../../common/components/h1';
import H2 from '../../common/components/h2';
import H3 from '../../common/components/h3';
import StackingButtons from '../../common/components/StackingButtons';
import TranslatedSpan from '../../common/components/TranslatedSpan';
import ValidationAlertMessage from '../../common/components/ValidationAlertMessage';
import capitalizeFirstLetter from '../../common/functions/capitalizeFirstLetter';
import capitalizeWords from '../../common/functions/capitalizeWords';
import translate from '../../common/functions/translate';
import { useConversion, useFormStart } from '../../common/hooks/use.analytics';
import { usePageTitle } from '../../common/hooks/usePageTitle';
import useTrait from '../../common/hooks/useTrait';
import { AccountUpdates, UpdateAccountResponse, useUpdateAccountApi } from '../../common/hooks/useUpdateAccountApi';
import { ApiError, hasInvalidField } from '../../common/functions/getApiErrorMessage';
import { SecretQuestionDropdown } from '../../common/components/SecretQuestionDropdown';
import { TermsAndConditionsToggle } from '../../common/components/TermsAndConditionsToggle';
import CancelButton from '../../common/components/CancelButton';
import { NewPassword } from '../../common/components/NewPassword/NewPassword';
import { AllFieldsRequired } from '../../common/components/AllFieldsRequired';
import { NewUsername } from '../../common/components/NewUsername/NewUsername';
import { scrollIntoViewAndFocus } from '../../common/functions/scrollIntoViewAndFocus';
import { HorizontalRule } from '../../common/components/HorizontalRule';

export type UpdateAccountErrorKeys = 'inconvenience' | 'tryToLog' | 'oneMoreTry' | 'tryAgain' | 'passwordGuidelines';

type UpdateAccountHeaderErrorKeys = 'completeItems' | 'cantProcess' | 'cantFind' | 'doesntMatch' | 'locked';

type UpdateAccountErrorListKeys =
    | "checkPassword"
    | "enterPassword"
    | "createPassword"
    | "enterEmail"
    | "createUsername"
    | "question"
    | "answer"
    | "alreadyUsed"
interface UpdateAccountSectionProps {
   temporaryUsername: string
   usernameVisible: boolean
   passwordVisible: boolean
   secretQuestionVisible: boolean
   emailVisible: boolean
   showPageCallBack: (page: number) => void
   resetLoginFlow: () => void,
   testInitialErrorState?: UpdateAccountErrorKeys & UpdateAccountErrorListKeys & 'isAlreadyUsed'
   testHeaders?: UpdateAccountHeaderErrorKeys
}

const UpdateAccountSection = (props: UpdateAccountSectionProps) => {
    const [agreementCheckedRef, setAgreementCheckedRef] = useState<HTMLElement | null>(null);
    const [confirmEmailAddressRef, setConfirmEmailAddressRef] = useState<HTMLElement | null>(null);
    const [confirmNewPasswordRef, setConfirmNewPasswordRef] = useState<HTMLElement | null>(null);
    const [currentPasswordRef, setCurrentPasswordRef] = useState<HTMLElement | null>(null);
    const [emailAddressRef, setEmailAddressRef] = useState<HTMLElement | null>(null);
    const [newPasswordRef, setNewPasswordRef] = useState<HTMLElement | null>(null);
    const [secretAnswerRef, setSecretAnswerRef] = useState<HTMLElement | null>(null);
    const [secretQuestionRef, setSecretQuestionRef] = useState<HTMLElement | null>(null);
    const [usernameRef, setUsernameRef] = useState<HTMLElement | null>(null);

    const [apiErrorHeaderKey, setApiErrorHeaderKey] = useState<UpdateAccountHeaderErrorKeys | null>(props.testHeaders || null);
    const [apiErrorHeading, setApiErrorHeading] = useState<string>();
    const [apiErrorKey, setApiErrorKey] = useState<UpdateAccountErrorKeys | null>(props.testInitialErrorState || null);
    const [apiErrorMessage, setApiErrorMessage] = useState<string | null>();
    const [apiErrorListKey, setApiErrorListKey] = useState<UpdateAccountErrorListKeys | null>(props.testInitialErrorState || null);
    const [apiErrorMessageList, setApiErrorMessageList] = useState<JSX.Element[]>([]);
    const [analyticsKey, setAnalyticsKey] = useState<UpdateAccountErrorKeys | UpdateAccountErrorListKeys | "unknownError">();

    const agreementChecked = useTrait(false);
    const agreementCheckedError = useTrait(false);
    const confirmEmailAddress = useTrait('');
    const confirmEmailAddressError = useTrait(false);
    const confirmNewPasswordError = useTrait(false);
    const currentPassword = useTrait('');
    const currentPasswordError = useTrait(false);
    const emailAddress = useTrait('');
    const emailAddressError = useTrait(false);
    const [newPassword, setNewPassword] = useState('');
    const newPasswordError = useTrait(false);
    const noEmailAddressChecked = useTrait(false);
    const secretAnswer = useTrait('');
    const secretAnswerError = useTrait(false);
    const secretQuestion = useTrait('');
    const secretQuestionError = useTrait(false);
    const [username, setUsername] = useState('');
    const validateEntireForm = useTrait(false);
    const validationMessages = useTrait<JSX.Element[]>([]);
    const updateAccountApi = useUpdateAccountApi();
    const formStart = useFormStart('updated_account');
    const conversion = useConversion('updated_account', 'updated_account');
    const counter = useTrait(0);
    const [isLoading, setIsLoading] = useState(false);
    const [alreadyUsed, setAlreadyUsed] = useState(false);
    const errorContainerRef = useRef<HTMLDivElement | null>(null);

    const currentListKey = useRef<string | null>(null);

    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [hasPasswordError, setHasPasswordError] = useState(false);
    const [hasConfirmPasswordError, setHasConfirmPasswordError] = useState(false);
    const [hasUsernameError, setHasUsernameError] = useState(false);
    usePageTitle(translate('update_account'));
    const header = translate('update_account');

    const {
        temporaryUsername,
        usernameVisible,
        passwordVisible,
        secretQuestionVisible,
        emailVisible,
    } = props;

    useEffect(() => {
        const errorKeyToMessage: {
          [key in UpdateAccountErrorKeys]: string
        } = {
            inconvenience: translate("we_are_sorry_for_inconvenience_try_updating_account_later"),
            tryToLog: translate("you_can_try_login_again"),
            oneMoreTry: translate("you_have_one_more_try_reenter_username"),
            tryAgain: translate("check_information_try_again"),
            passwordGuidelines: translate("the_password_does_not_meet_required_enter_correct_info"),
        }
        setApiErrorMessage(apiErrorKey ? errorKeyToMessage[apiErrorKey] : null);
    }, [apiErrorKey, newPasswordRef]);

    useEffect(() => {
        const errorKeyToMessage: {
            [key in UpdateAccountErrorListKeys]: JSX.Element
        } = {
            checkPassword: <>
                <ValidationAlertMessage
                    description={translate("check_current_password")}
                    fieldReference={newPasswordRef}
                    linkText={translate("password")}
                    componentHeading={header}
                />
            </>,
            enterPassword: <>
                <ValidationAlertMessage
                    description={translate("enter_new_password_different_from_last_six")}
                    fieldReference={newPasswordRef}
                    linkText={translate("password")}
                    componentHeading={header}
                />
            </>,
            createPassword: <>
                <ValidationAlertMessage
                    description={translate("create_password_for_your_account")}
                    fieldReference={newPasswordRef}
                    linkText={translate("password")}
                    componentHeading={header}
                />
            </>,
            enterEmail: <>
                <ValidationAlertMessage
                    description={translate('enter_email_or_select_do_not_have')}
                    fieldReference={emailAddressRef}
                    linkText={translate("email_address")}
                    componentHeading={header}
                />
            </>,
            createUsername: <>
                <ValidationAlertMessage
                    description={translate("create_username_for_your_account")}
                    fieldReference={usernameRef}
                    linkText={translate("username")}
                    componentHeading={header}
                />
            </>,
            alreadyUsed: <>
                <ValidationAlertMessage
                    description={translate("this_username_already_used")}
                    fieldReference={usernameRef}
                    linkText={translate("username")}
                    componentHeading={header}
                />
            </>,
            question: <>
                <ValidationAlertMessage
                    description={translate("select_secret_question")}
                    fieldReference={secretQuestionRef}
                    linkText={translate("secret_question")}
                    componentHeading={header}
                />
            </>,
            answer: <>
                <ValidationAlertMessage
                    description={translate("enter_answer_to_your_secret_question")}
                    fieldReference={secretAnswerRef}
                    linkText={translate("secret_answer")}
                    componentHeading={header}
                />
            </>,
        }
        if (currentListKey.current === apiErrorListKey) {
            return;
        }
        if (apiErrorListKey && apiErrorListKey in errorKeyToMessage) {
            setApiErrorMessageList([errorKeyToMessage[apiErrorListKey], ...apiErrorMessageList]);
        } else {
            setApiErrorMessageList([]);
        }
        currentListKey.current = apiErrorListKey;
    }, [apiErrorListKey, apiErrorMessageList, newPasswordRef, emailAddressRef, usernameRef, secretQuestionRef, secretAnswerRef, header]);


    useEffect(() => {
        const errorHeaderKeyToMessage: {
          [key in UpdateAccountHeaderErrorKeys]: string
        } = {
            completeItems: translate("complete_items_to_update_account"),
            cantProcess: translate("we_cant_process_request"),
            cantFind: translate("we_cant_find_you"),
            doesntMatch: translate("your_username_password_not_match"),
            locked: translate("your_account_now_locked_protect_information"),
        }
        setApiErrorHeading(apiErrorHeaderKey ? errorHeaderKeyToMessage[apiErrorHeaderKey] : '');
    }, [apiErrorHeaderKey, apiErrorMessage]);

    useEffect(() => {
        const errorKeyToMessage: {
          [key in UpdateAccountErrorKeys | UpdateAccountErrorListKeys | "unknownError"]: string
        } = {
            inconvenience: "We're sorry for any inconvenience. Try updating your account later.",
            tryToLog: "Your account is now locked to protect your information. You can try to log in again in 1 hour.",
            oneMoreTry: "You have 1 more try to re-enter your username and password. If it doesn't match our records, your account will be locked for 1 hour.",
            tryAgain: "Check the information you entered and try again.",
            passwordGuidelines: "The Password does not meet the required guidelines. Please enter the correct information and try again.",
            unknownError: 'unknown error',
            checkPassword: "Check your current password.",
            enterPassword: "Enter a new password that's different from your last 6 passwords.",
            createPassword: "Create a password for your account using the guidelines listed.",
            enterEmail: 'Enter your email address or select "I don\'t have an email address."',
            createUsername: "Create a username for your account using the guidelines listed.",
            alreadyUsed: "This username is already being used by someone else. Try creating another username, using the guidelines listed.",
            question: "Select a secret question.",
            answer: "Enter the answer to your secret question.",
        }
        if (analyticsKey) {
            conversion.failAPI(analyticsKey ? errorKeyToMessage[analyticsKey] : '');
        }
    }, [conversion, analyticsKey]);

    const getUpdateAccountButton = () => {
        return <>
            <Button
                variation={'solid'}
                mfaAnalyticsData={{
                    text: translate('update_account'),
                    parentComponentHeading: translate('update_account')
                }}
                onClick={handleUpdateAccountButtonClick}
                isLoading={isLoading}
            >
                <TranslatedSpan english={'update_account'}/>
            </Button>
        </>;
    };

    const getValidationMessages = () => {
        const messages: JSX.Element[] = [];
        if (!validateEntireForm.get())
            return messages;
        if (usernameVisible && hasUsernameError && username === '') {
            messages.push(
                <ValidationAlertMessage
                    description={translate('enter_your_new_username')}
                    fieldReference={usernameRef}
                    linkText={translate('username')}
                    componentHeading={header}
                />
            );
        }
        if (username !== '' && hasUsernameError) {
            messages.push(
                <ValidationAlertMessage
                    description={translate('create_username_for_your_account')}
                    fieldReference={usernameRef}
                    linkText={translate('username')}
                    componentHeading={header}
                />
            );
        }
        if (passwordVisible && !validateCurrentPassword()) {
            messages.push(
                <ValidationAlertMessage
                    description={translate('enter_your_password')}
                    fieldReference={currentPasswordRef}
                    linkText={translate('current_password')}
                    componentHeading={header}
                />
            );
        }
        if (passwordVisible && hasPasswordError) {
            messages.push(
                <ValidationAlertMessage
                    description={newPassword.length ? translate('create_password_for_your_account') : translate('enter_your_new_password')}
                    fieldReference={newPasswordRef}
                    linkText={capitalizeWords(translate('new_password'))}
                    componentHeading={header}
                />
            );
        }
        if (passwordVisible && hasConfirmPasswordError) {
            messages.push(
                <ValidationAlertMessage
                    description={translate('enter_your_new_password')}
                    fieldReference={confirmNewPasswordRef}
                    linkText={capitalizeWords(translate('confirm_new_password'))}
                    componentHeading={header}
                />
            );
        }
        if (secretQuestionVisible && !validateSecretQuestion()) {
            messages.push(
                <ValidationAlertMessage
                    description={translate('pick_secret_question')}
                    fieldReference={secretQuestionRef}
                    linkText={translate('secret_question')}
                    componentHeading={header}
                />
            );
        }
        if (secretQuestionVisible && !validateSecretAnswer()) {
            messages.push(
                <ValidationAlertMessage
                    description={translate('enter_answer_to_your_secret_question')}
                    fieldReference={secretAnswerRef}
                    linkText={capitalizeWords(translate('secret_answer'))}
                    componentHeading={header}
                />
            );
        }
        if (emailVisible && !validateEmailAddress()) {
            messages.push(
                <ValidationAlertMessage
                    description={translate('enter_email_or_select_do_not_have')}
                    fieldReference={emailAddressRef}
                    linkText={translate('email_address')}
                    componentHeading={header}
                />
            );
        }
        if (emailVisible && !validateConfirmEmailAddress()) {
            messages.push(
                <ValidationAlertMessage
                    description={translate("reenter_email_address_or_select_do_not_have")}
                    fieldReference={confirmEmailAddressRef}
                    linkText={translate('confirm_email_address')}
                    componentHeading={header}
                />
            );
        }
        if (usernameVisible && passwordVisible && secretQuestionVisible && emailVisible && !validateAgreementChecked()) {
            messages.push(
                <ValidationAlertMessage
                    description={translate('review_and_check_to_next_step')}
                    fieldReference={agreementCheckedRef}
                    linkText={capitalizeFirstLetter(translate('terms_and_conditions'))}
                    componentHeading={header}
                />
            );
        }
        return messages;
    };

    const reset = () => {
        setApiErrorKey(null);
        setApiErrorHeaderKey(null);
        setApiErrorListKey(null);
        newPasswordError.set(false);
        confirmNewPasswordError.set(false);
        setAlreadyUsed(false);
    };

    const handleUpdateAccountButtonClick = async () => {
        reset();
        setSubmitAttempted(true);
        if (isLoading) {
            return;
        }
        setIsLoading(true);
        conversion.attempt();
        const errorFieldRef = validateForm();
        if (errorFieldRef != null) {
            scrollIntoViewAndFocus(errorFieldRef);
            setIsLoading(false);
            return;
        }
        const getPostBody = () => {
            const updates: AccountUpdates = {};

            const challengeQuestionAndAnswers = [];
            if (usernameVisible) {
                updates['username'] = username;
            }
            if (passwordVisible) {
                updates['password'] = newPassword;
            }
            if (emailVisible) {
                updates['email'] = emailAddress.get();
            }
            if (secretQuestionVisible) {
                challengeQuestionAndAnswers.push({"question": secretQuestion.get(), "answer": secretAnswer.get()});
                updates['challengeQuestionAndAnswers'] = challengeQuestionAndAnswers;
            }
            return updates;
        };
        const headers = {
            headers: {
                'accept': '*/*',
                'content-type': 'application/json'
            }
        };

        const updateAccountArgs = {
            temporaryUsername,
            temporaryPassword: currentPassword.get(),
            updates: getPostBody(),
            headers
        }
        const updateAccountResponse: UpdateAccountResponse = await updateAccountApi.updateAccount(updateAccountArgs);
        // updateAccountResponse returns undefined if already in flight
        if (!updateAccountResponse) {
            return;
        }
        setIsLoading(false);
        const unknownErrorMessage = "We can't process your request at this time. We're sorry for any inconvenience. Please try again.";
        if (!updateAccountResponse?.code) {
            setAnalyticsKey('unknownError');
            setApiErrorMessage(unknownErrorMessage);
            return;
        }
        reset();
        handleUpdateAccountResponseCodes(updateAccountResponse);
    }

    function handleUpdateAccountResponseCodes(updateAccountResponse: any) {
        if(updateAccountResponse.code !== 204){
            scrollIntoViewAndFocus(errorContainerRef.current);
        }
        switch (updateAccountResponse.code) {
            case 204:
                conversion.success();
                props.showPageCallBack(1);
                break;
            case 400:
                handle400Errors(updateAccountResponse);
                break;
            case 401:
                const count = counter.set(counter.get() + 1);
                if (count <= 3) {
                    setApiErrorHeaderKey('doesntMatch');
                    setApiErrorKey('tryAgain');
                    setAnalyticsKey('tryAgain');
                }
                else if (count === 4) {
                    setApiErrorHeaderKey('doesntMatch');
                    setApiErrorKey('oneMoreTry');
                    setAnalyticsKey('oneMoreTry');
                }
                else {
                    setApiErrorHeaderKey('locked');
                    setApiErrorKey('tryToLog');
                    setAnalyticsKey('tryToLog');
                }
                break;
            case 403:
                setApiErrorHeaderKey('locked');
                setApiErrorKey('tryToLog');
                setAnalyticsKey('tryToLog');
                break;
            case 409:
                setApiErrorHeaderKey('completeItems');
                setApiErrorListKey('alreadyUsed');
                setAnalyticsKey('alreadyUsed');
                break;
            case 404:
            default:
                setApiErrorHeaderKey('cantProcess');
                setApiErrorKey('inconvenience');
                setAnalyticsKey('inconvenience');
                break;
        }
    }

    const handle400Errors = (updateAccountResponse: any) => {
        const isAlreadyUsed = updateAccountResponse.errors?.some((error: ApiError) => error.name === 'AlreadyUsed');
        const invalidUsername = hasInvalidField(updateAccountResponse.errors, 'username') || hasInvalidField(updateAccountResponse.errors, 'updates.username');
        const invalidPassword = hasInvalidField(updateAccountResponse.errors, 'password');
        const invalidUpdatesPassword = hasInvalidField(updateAccountResponse.errors, 'updates.password');
        const invalidChallengeQuestionAndAnswers = hasInvalidField(updateAccountResponse.errors, 'updates.challengeQuestionAndAnswers');
        const invalidLang = hasInvalidField(updateAccountResponse.errors, 'lang');
        const invalidUpdates = hasInvalidField(updateAccountResponse.errors, 'updates');
        const notFound = updateAccountResponse.errors?.some((error: ApiError) => error.message && error.message.includes('user not found'));

        if (hasInvalidField(updateAccountResponse?.errors, 'updates.email')) {
            setApiErrorHeaderKey('completeItems');
            setApiErrorListKey('enterEmail');
            setAnalyticsKey('enterEmail');
        } else if (isAlreadyUsed) {
            setAlreadyUsed(true);
            newPasswordError.set(true);
            confirmNewPasswordError.set(true);
            setApiErrorHeaderKey('completeItems');
            setApiErrorListKey('enterPassword');
            setAnalyticsKey('enterPassword');
        } else if (invalidUsername) {
            setApiErrorHeaderKey('completeItems');
            setApiErrorListKey('createUsername');
            setAnalyticsKey('createUsername');
        } else if (invalidPassword) {
            setApiErrorHeaderKey('completeItems');
            setApiErrorListKey('checkPassword');
            setAnalyticsKey('checkPassword');
        } else if (invalidUpdatesPassword) {
            setApiErrorHeaderKey('completeItems');
            setApiErrorListKey('createPassword');
            setAnalyticsKey('createPassword');
        } else if (invalidChallengeQuestionAndAnswers) {
            setApiErrorHeaderKey('completeItems');
            setApiErrorListKey('question');
            setApiErrorListKey('answer');
            setAnalyticsKey('question');
            setAnalyticsKey('answer');
        } else if (invalidLang || invalidUpdates) {
            setApiErrorHeaderKey('cantProcess');
            setApiErrorKey('inconvenience');
            setAnalyticsKey('inconvenience');
        } else if (notFound) {
            setApiErrorHeaderKey('cantFind');
            setApiErrorKey('tryAgain');
            setAnalyticsKey('tryAgain');
        }
        else {
            setApiErrorHeaderKey('completeItems');
            setApiErrorKey('inconvenience');
            setAnalyticsKey('inconvenience');
        }

    }

    const updateUsername = (value: string) => {
        formStart();
        setUsername(value);
    };

    const updateCurrentPassword = (value: string) => {
        formStart();
        const newValue = value.trimStart();
        currentPassword.set(newValue);
        validateCurrentPassword();
        if (validateEntireForm.get())
            validateForm();
    };

    const updateNewPassword = (value: string) => {
        formStart();
        setNewPassword(value);
        if (validateEntireForm.get())
            validateForm();
    };

    const updateConfirmNewPassword = () => {
        formStart();
        if (validateEntireForm.get())
            validateForm();
    };

    const updateSecretQuestion = (value: string) => {
        formStart();
        const newValue = value.trimStart();
        secretQuestion.set(newValue);
        validateSecretQuestion();
        if (validateEntireForm.get())
            validateForm();
    };

    const updateSecretAnswer = (value: string) => {
        formStart();
        const newValue = value.trimStart();
        secretAnswer.set(newValue);
        validateSecretAnswer();
        if (validateEntireForm.get())
            validateForm();
    };

    const updateEmailAddress = (value: string) => {
        formStart();
        const newValue = value.trimStart();
        emailAddress.set(newValue);
        validateEmailAddress();
        if (validateEntireForm.get())
            validateForm();
    };

    const updateNoEmailAddressChecked: ChangeEventHandler<HTMLInputElement> = (event) => {
        formStart();
        if (event.type === 'change') {
            if (!noEmailAddressChecked.get()) {
                emailAddress.set('');
                emailAddressError.set(false);
                confirmEmailAddress.set('');
                confirmEmailAddressError.set(false);
            }
            noEmailAddressChecked.set(!noEmailAddressChecked.get());
        }
        if (validateEntireForm.get())
            validateForm();
    };

    const updateAgreementChecked: ChangeEventHandler<HTMLInputElement> = (event) => {
        formStart();
        if (event.type === 'change')
            agreementChecked.set(!agreementChecked.get());
        validateAgreementChecked();
        if (validateEntireForm.get())
            validateForm();
    };

    const updateConfirmEmailAddress = (value: string) => {
        formStart();
        const newValue = value.trimStart();
        confirmEmailAddress.set(newValue);
        validateConfirmEmailAddress();
        if (validateEntireForm.get())
            validateForm();
    };

    const validateAgreementChecked = () => {
        const isValid = agreementChecked.get();
        if (agreementCheckedError.get() === isValid)
            agreementCheckedError.set(!isValid);
        return isValid;
    };

    const validateConfirmEmailAddress = () => {
        const isValid = confirmEmailAddress.get() !== '' && emailAddress.get() === confirmEmailAddress.get();
        if (confirmEmailAddressError.get() === isValid)
            confirmEmailAddressError.set(!isValid);
        return noEmailAddressChecked.get() || isValid;
    };

    const validateCurrentPassword = () => {
        const isValid = currentPassword.get() !== '';
        if (currentPasswordError.get() === isValid)
            currentPasswordError.set(!isValid);
        return isValid;
    };

    const validateEmailAddress = () => {
        const isValid = emailAddress.get() !== '';
        if (emailAddressError.get() === isValid)
            emailAddressError.set(!isValid);
        return noEmailAddressChecked.get() || isValid;
    };

    const validateForm = (): HTMLElement | null | undefined => {
        if (!validateEntireForm.get())
            validateEntireForm.set(true);
        let errorFieldRef = null;
        if (usernameVisible && hasUsernameError)
            errorFieldRef = usernameRef;
        if (passwordVisible && !validateCurrentPassword())
            errorFieldRef = currentPasswordRef;
        if (passwordVisible && hasPasswordError)
            errorFieldRef = newPasswordRef;
        if (passwordVisible && hasConfirmPasswordError)
            errorFieldRef = confirmNewPasswordRef;
        if (secretQuestionVisible && !validateSecretQuestion())
            errorFieldRef = secretQuestionRef;
        if (secretQuestionVisible && !validateSecretAnswer())
            errorFieldRef = secretAnswerRef;
        if (emailVisible && !validateEmailAddress())
            errorFieldRef = emailAddressRef;
        if (emailVisible && !validateConfirmEmailAddress())
            errorFieldRef = confirmEmailAddressRef;
        if (usernameVisible && passwordVisible && secretQuestionVisible && emailVisible && !validateAgreementChecked())
            errorFieldRef = agreementCheckedRef;
        const messages = getValidationMessages();
        validationMessages.set(messages);
        if (messages.length) {
            const errorString = messages.map(x => x.props.description).join(', ');
            conversion.failValidation(errorString);
        }
        return messages.length ? errorFieldRef : null;
    };

    const validateSecretAnswer = () => {
        const isValid = secretAnswer.get() !== '';
        if (secretAnswerError.get() === isValid)
            secretAnswerError.set(!isValid);
        return isValid;
    };

    const validateSecretQuestion = () => {
        const isValid = secretQuestion.get() !== '';
        if (secretQuestionError.get() === isValid)
            secretQuestionError.set(!isValid);
        return isValid;
    };

    const getApiErrorMessage = () => {
        if (!apiErrorMessage)
            return null;
        if (typeof apiErrorMessage === 'string') {
            return apiErrorMessage;
        }
        return apiErrorMessage;
    };

    return <>
        <Div
            isContainer={true}
            paddingySM={1} paddingyMD={2} paddingyLG={3} paddingyXL={4}
        >
            <fieldset style={{
                border: 0,
                margin: 0,
                padding: 0,
            }}>
                <legend>
                    <Div isRow>
                        <Div cols={12}>
                            <H1>
                                {capitalizeFirstLetter(translate('update_account').toLowerCase())}
                            </H1>
                        </Div>
                    </Div>
                </legend>
                <Div isRow>
                    <Div colsMD={8} cols={12} marginTop={3}>
                        <Alert hideIcon={true}>
                            <p className="ds-c-alert__text">
                                <TranslatedSpan english={'your_account_need_to_update'}/>
                            </p>
                        </Alert>
                        <div></div>
                    </Div>
                </Div>
                <Div marginTop={3} marginBottom={3}>
                    <AllFieldsRequired />
                </Div>
                <div ref={errorContainerRef} tabIndex={-1}>
                    {(apiErrorMessage || apiErrorMessageList.length > 0) &&
                        <ErrorAlert
                            className="ds-u-margin-bottom--3"
                            heading={apiErrorHeading}
                            errorMessage={getApiErrorMessage()}
                            validationMessages={apiErrorMessageList} />
                    }
                    {validationMessages.get().length !== 0 &&
                        <ErrorAlert
                            className="ds-u-margin-bottom--3"
                            headingId={'validationAlertHeader'}
                            heading={translate('complete_items_to_update_account')}
                            validationMessages={validationMessages.get()}
                        />}
                </div>

                {/* Username section */}
                {usernameVisible &&
                    <>
                        <Div isRow>
                            <Div cols={12} colsMD={10}>
                                <Div>
                                    <H2>
                                        <TranslatedSpan english={'update_username'}/>
                                    </H2>
                                </Div>
                                <NewUsername
                                    component={'updateAccountSection'}
                                    label={translate('new_username')}
                                    onUsernameChange={updateUsername}
                                    onUsernameError={setHasUsernameError}
                                    submitAttempted={submitAttempted}
                                    inputRef={setUsernameRef}
                                />
                            </Div>
                        </Div>
                        {(passwordVisible || secretQuestionVisible || emailVisible) && <HorizontalRule />}
                    </>
                }

                {/* Password section */}
                {passwordVisible &&
                    <>
                        <Div isRow>
                            <Div cols={12} colsMD={10}>
                                <Div paddingTop={2}>
                                    <H2 className={'ds-u-margin-top--1'}>
                                        <TranslatedSpan english={'current_password'}/>
                                    </H2>
                                </Div>
                                <Div className='ds-u-margin-top--2 ds-u-padding-bottom--0'>
                                    {translate('enter_temporary_password_if_you_were_given_one')}
                                </Div>
                                <PasswordSection
                                    errorMessage={currentPasswordError.get() ? translate('enter_your_password') : null}
                                    inputRef={setCurrentPasswordRef}
                                    label={translate('current_password')}
                                    onBlur={event => updateCurrentPassword(event.currentTarget.value)}
                                    onChange={event => updateCurrentPassword(event.currentTarget.value)}
                                    showForgotPasswordLink={false}
                                    value={currentPassword.get()}
                                    labelClassName="ds-u-margin-top--1"
                                />
                            </Div>
                        </Div>
                        <HorizontalRule />
                        <Div isRow>
                            <Div cols={12} colsMD={10}>
                                <Div paddingTop={2}>
                                    <H2 className={'ds-u-margin-top--1'}>
                                        <TranslatedSpan english={'update_password'}/>
                                    </H2>
                                </Div>
                                <NewPassword
                                    component={'updateAccount'}
                                    onPasswordChange={updateNewPassword}
                                    onConfirmPasswordChange={updateConfirmNewPassword}
                                    onPasswordError={setHasPasswordError}
                                    onConfirmPasswordError={setHasConfirmPasswordError}
                                    passwordRef={setNewPasswordRef}
                                    confirmPasswordRef={setConfirmNewPasswordRef}
                                    passwordLabel={translate('new_password')}
                                    confirmPasswordLabel={translate('confirm_new_password')}
                                    submitAttempted={submitAttempted}
                                    showAlreadyUsedError={alreadyUsed}
                                />
                            </Div>
                        </Div>
                        {(secretQuestionVisible || emailVisible) && <HorizontalRule />}
                    </>
                }

                {/* Secret Question section */}
                {secretQuestionVisible &&
                <>
                    <Div isRow>
                        <Div cols={12} colsMD={10}>
                            <Div paddingTop={2}>
                                <H2 className={'ds-u-margin-top--1'}>
                                    <TranslatedSpan english={'update_secret_question'}/>
                                </H2>
                            </Div>
                            <SecretQuestionDropdown
                                context={secretQuestionError.get() ? 'login' : null}
                                inputRef={setSecretQuestionRef}
                                onBlur={event => updateSecretQuestion(event.currentTarget.value)}
                                onChange={event => updateSecretQuestion(event.currentTarget.value)}
                                value={secretQuestion.get()}
                            />
                            <TextField
                                errorMessage={secretAnswerError.get() ? translate('enter_answer_to_your_secret_question') : null}
                                inputRef={setSecretAnswerRef}
                                label={translate('secret_answer')}
                                name={'secretAnswer'}
                                onClick={formStart}
                                onBlur={event => updateSecretAnswer(event.currentTarget.value)}
                                onChange={event => updateSecretAnswer(event.currentTarget.value)}
                                value={secretAnswer.get()}
                            />
                        </Div>
                    </Div>
                    {emailVisible && <HorizontalRule />}
                </>}

                {/* Add email section */}
                {emailVisible &&
                <Div>
                    <Div isRow>
                        <Div cols={12} colsMD={10}>
                            <Div paddingTop={2}>
                                <H2 className={'ds-u-margin-top--1'}>
                                    {translate('add_email')}
                                </H2>
                            </Div>
                            <TextField
                                disabled={noEmailAddressChecked.get()}
                                errorMessage={emailAddressError.get() ? translate('enter_email_or_select_do_not_have') : null}
                                inputRef={setEmailAddressRef}
                                label={translate('email_address')}
                                name={'emailAddress'}
                                onClick={formStart}
                                onBlur={event => updateEmailAddress(event.currentTarget.value)}
                                onChange={event => updateEmailAddress(event.currentTarget.value)}
                                value={emailAddress.get()}
                            />
                            <TextField
                                disabled={noEmailAddressChecked.get()}
                                errorMessage={confirmEmailAddressError.get() ? translate("reenter_email_address_or_select_do_not_have") : null}
                                inputRef={setConfirmEmailAddressRef}
                                label={translate('confirm_email_address')}
                                name={'confirmEmailAddress'}
                                onClick={formStart}
                                onBlur={event => updateConfirmEmailAddress(event.currentTarget.value)}
                                onChange={event => updateConfirmEmailAddress(event.currentTarget.value)}
                                value={confirmEmailAddress.get()}
                            />
                        </Div>
                    </Div>
                    <Div isRow>
                        <Div cols={12}>
                            <Div paddingTop={2}>
                                <h3 className={"ds-text-heading--md"}>
                                    <TranslatedSpan english={'not_have_an_email_address'}/>
                                </h3>
                            </Div>
                            <p className={'ds-u-margin-top--1 ds-u-margin-bottom--0'}>
                                <TranslatedSpan english={'we_will_send_info_by_mail'}/>
                            </p>
                            <Checkbox
                                checked={noEmailAddressChecked.get()}
                                className={'ds-u-margin-top--0'}
                                name={'noEmailAddressCheckbox'}
                                onBlur={event => updateNoEmailAddressChecked(event)}
                                onChange={event => updateNoEmailAddressChecked(event)}
                                type={'checkbox'}
                                title={translate('do_not_have_email_address')}
                                value={'noEmailAddress'}
                            >
                            </Checkbox>
                        </Div>
                    </Div>
                </Div>}

                {/* Agree to statements section */}
                {usernameVisible && passwordVisible && secretQuestionVisible && emailVisible &&
                <>
                    <HorizontalRule marginBottom={2} />
                    <Div isRow>
                        <Div cols={12}>
                            <Div paddingTop={2}>
                                <H3>
                                    <TranslatedSpan english={'agree_to_statement_below'}/>
                                </H3>
                            </Div>
                            <Div>
                                <Checkbox
                                    aria-invalid={agreementCheckedError.get().toString()}
                                    checked={agreementChecked.get()}
                                    className={'ds-u-margin-top--0'}
                                    errorMessage={agreementCheckedError.get() ? translate('review_and_check_to_next_step') : null}
                                    inputRef={setAgreementCheckedRef}
                                    name={'agreementCheckbox'}
                                    onBlur={event => updateAgreementChecked(event)}
                                    onChange={event => updateAgreementChecked(event)}
                                    type={'checkbox'}
                                    value={'agreed'}
                                    title={translate('check_the_box_to_agree')}
                                >
                                    <Div colsLG={8} cols={12} marginLeft={3} paddingRight={4}>
                                        <TermsAndConditionsToggle />
                                    </Div>
                                </Checkbox>
                            </Div>
                        </Div>
                    </Div>
                </>}

                <StackingButtons
                    button1={<CancelButton onClick={() => props.resetLoginFlow()} />}
                    button2={getUpdateAccountButton()}
                />

            </fieldset>
        </Div>
    </>;
}

export default UpdateAccountSection;
